/**
 * Full-Page Chat Settings Page
 *
 * Configure the ChatGPT-style full-page chat channel.
 * Settings stored in chatbot.settings.fullpage_chat JSON.
 * Split-pane: settings left, live preview iframe right.
 */

import React, { useCallback, useState } from 'react'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { api } from '../../../services/api'
import { useToast } from '../../../hooks/useToast'
import { API_BASE_URL } from '../../../utils/constants'
import {
  ArrowLeft, Save, ExternalLink, Copy, Check,
  Plus, X, GripVertical, Loader2, Eye,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface FullPageChatSettings {
  enabled: boolean
  page_title: string
  welcome_message: string
  welcome_subtitle: string
  company_logo: string
  favicon: string
  suggestions: string[]
  theme: string
  primary_color: string
  show_branding: boolean
}

const DEFAULT_SETTINGS: FullPageChatSettings = {
  enabled: true,
  page_title: '',
  welcome_message: '',
  welcome_subtitle: 'Ask me anything — I\'m here to help.',
  company_logo: '',
  favicon: '',
  suggestions: [],
  theme: 'light',
  primary_color: '#3b82f6',
  show_branding: true,
}

const COLOR_PRESETS = [
  '#3b82f6', // Blue
  '#8b5cf6', // Violet
  '#6366f1', // Indigo
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#000000', // Black
]

export function FullPageChatSettingsPage() {
  const { chatbot, refetch } = useAgentContext()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [settings, setSettings] = useState<FullPageChatSettings>(() => {
    const existing = (chatbot as unknown as Record<string, Record<string, unknown>>).settings?.fullpage_chat as Partial<FullPageChatSettings> | undefined
    return { ...DEFAULT_SETTINGS, ...existing }
  })
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [newSuggestion, setNewSuggestion] = useState('')

  // Generate hosted URL
  const hostedUrl = `${API_BASE_URL}/chat/${chatbot.id}`
  const previewUrl = `/fullpage-chat.html?chatbotId=${chatbot.id}`

  const updateField = useCallback(<K extends keyof FullPageChatSettings>(
    key: K,
    value: FullPageChatSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const currentSettings = ((chatbot as unknown as Record<string, unknown>).settings || {}) as Record<string, unknown>
      await api.patch(`/chatbots/${chatbot.id}`, {
        settings: {
          ...currentSettings,
          fullpage_chat: settings,
        },
      })
      await refetch()
      toast({ title: 'Settings saved', variant: 'success' })
    } catch {
      toast({ title: 'Failed to save settings', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleAddSuggestion = () => {
    const text = newSuggestion.trim()
    if (!text || settings.suggestions.includes(text)) return
    updateField('suggestions', [...settings.suggestions, text])
    setNewSuggestion('')
  }

  const handleRemoveSuggestion = (index: number) => {
    updateField('suggestions', settings.suggestions.filter((_, i) => i !== index))
  }

  const handleCopy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
    toast({ title: 'Copied to clipboard', variant: 'success', duration: 2000 })
  }

  const embedCode = `<script src="${API_BASE_URL}/static/fullpage-chat-embed.js"></script>
<script>
  CleverActChat.init({
    chatbotId: '${chatbot.id}',
    apiUrl: '${API_BASE_URL}'
  });
</script>`

  const iframeCode = `<iframe
  src="${hostedUrl}"
  style="width:100%;height:600px;border:none;border-radius:12px"
  allow="clipboard-write"
  title="Chat"
></iframe>`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/dashboard/chatbots/${chatbot.id}/deploy`)}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Full-Page Chat</h1>
            <p className="text-sm text-muted-foreground">
              A standalone chat page your end users can access directly
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(previewUrl, '_blank')}
            className="gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Settings column */}
        <div className="space-y-4">
          {/* Enable/Disable */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">Enable Full-Page Chat</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Make the hosted chat page publicly accessible
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => updateField('enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-muted peer-checked:bg-primary rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Identity */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-medium">Identity</h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Page Title</label>
                  <input
                    type="text"
                    value={settings.page_title}
                    onChange={(e) => updateField('page_title', e.target.value)}
                    placeholder={chatbot.name}
                    className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Welcome Message</label>
                  <input
                    type="text"
                    value={settings.welcome_message}
                    onChange={(e) => updateField('welcome_message', e.target.value)}
                    placeholder={`Hi! I'm ${chatbot.name}`}
                    className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Subtitle</label>
                  <input
                    type="text"
                    value={settings.welcome_subtitle}
                    onChange={(e) => updateField('welcome_subtitle', e.target.value)}
                    placeholder="Ask me anything — I'm here to help."
                    className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Company Logo URL</label>
                  <input
                    type="url"
                    value={settings.company_logo}
                    onChange={(e) => updateField('company_logo', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suggestions */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-medium">Suggestions</h3>
              <p className="text-xs text-muted-foreground">
                Starter prompts shown on the welcome screen
              </p>

              {settings.suggestions.map((suggestion, index) => (
                <div key={index} className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
                  <span className="flex-1 text-sm truncate">{suggestion}</span>
                  <button
                    onClick={() => handleRemoveSuggestion(index)}
                    className="shrink-0 p-1 rounded hover:bg-muted"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
              ))}

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSuggestion}
                  onChange={(e) => setNewSuggestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSuggestion()}
                  placeholder="Add a suggestion..."
                  className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
                />
                <Button variant="outline" size="sm" onClick={handleAddSuggestion} disabled={!newSuggestion.trim()}>
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-medium">Appearance</h3>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Theme</label>
                <div className="flex gap-2 mt-1">
                  {(['light', 'dark', 'system'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => updateField('theme', t)}
                      className={`px-3 py-1.5 rounded-lg border text-sm capitalize transition-colors ${
                        settings.theme === t
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input hover:bg-muted'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Primary Color</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Accent color for buttons, links, and user messages
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5 flex-wrap">
                    {COLOR_PRESETS.map((color) => (
                      <button
                        key={color}
                        onClick={() => updateField('primary_color', color)}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          settings.primary_color === color
                            ? 'border-foreground scale-110'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 ml-2">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateField('primary_color', e.target.value)}
                      className="w-7 h-7 rounded cursor-pointer border border-input"
                    />
                    <input
                      type="text"
                      value={settings.primary_color}
                      onChange={(e) => updateField('primary_color', e.target.value)}
                      placeholder="#3b82f6"
                      className="w-20 rounded-lg border border-input bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-ring font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Show Branding</label>
                  <p className="text-xs text-muted-foreground">
                    "Powered by CleverAct AI" at the bottom
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_branding}
                    onChange={(e) => updateField('show_branding', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-muted peer-checked:bg-primary rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Integration */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-medium">Integration</h3>

              {/* Hosted URL */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Hosted Link</label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 rounded-lg border bg-muted/50 px-3 py-2 text-xs break-all">
                    {hostedUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(hostedUrl, 'url')}
                  >
                    {copied === 'url' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              {/* Embed Code */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Embed Script</label>
                <div className="mt-1 relative">
                  <pre className="rounded-lg border bg-muted/50 px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
                    {embedCode}
                  </pre>
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleCopy(embedCode, 'embed')}
                  >
                    {copied === 'embed' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              {/* Iframe Code */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Iframe</label>
                <div className="mt-1 relative">
                  <pre className="rounded-lg border bg-muted/50 px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
                    {iframeCode}
                  </pre>
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleCopy(iframeCode, 'iframe')}
                  >
                    {copied === 'iframe' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview column */}
        <div className="hidden xl:block sticky top-6">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <span className="text-xs font-medium text-muted-foreground">Live Preview</span>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-xs"
                onClick={() => window.open(previewUrl, '_blank')}
              >
                <ExternalLink className="w-3 h-3" />
                Open
              </Button>
            </div>
            <div className="aspect-[4/3] bg-muted">
              <iframe
                src={hostedUrl}
                className="w-full h-full border-none"
                title="Full-page chat preview"
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
